import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useApp } from '@/contexts/AppContext';
import { getForecastRunStatus } from '@/api/services/forecastService';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ForecastRefreshStatus = 'idle' | 'in-progress' | 'complete';

export interface ForecastRefreshState {
  status: ForecastRefreshStatus;
  startTime: Date | null;
  completionTime: Date | null;
  triggeredBy: string;        // Display name of the user who triggered it
  estimatedDurationMs: number; // Estimated duration in milliseconds
}

interface ForecastRefreshContextType {
  refreshState: ForecastRefreshState;
  startRefresh: (triggeredBy: string, estimatedDurationMs: number) => void;
  completeRefresh: () => void;
  resetRefresh: () => void;
}

// ─── Context ─────────────────────────────────────────────────────────────────

const ForecastRefreshContext = createContext<ForecastRefreshContextType | undefined>(undefined);

const INITIAL_STATE: ForecastRefreshState = {
  status: 'idle',
  startTime: null,
  completionTime: null,
  triggeredBy: '',
  estimatedDurationMs: 0,
};

const POLLING_INTERVAL_MS = 3_000;

export const ForecastRefreshProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { mode } = useApp();
  const [refreshState, setRefreshState] = useState<ForecastRefreshState>(INITIAL_STATE);
  // Timer ref so we can cancel the auto-dismiss on reset
  const autoHideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const refreshStateRef = useRef<ForecastRefreshState>(INITIAL_STATE);

  // Keep the ref in sync with state so polling callback has latest value
  useEffect(() => {
    refreshStateRef.current = refreshState;
  }, [refreshState]);

  const startRefresh = useCallback((triggeredBy: string, estimatedDurationMs: number) => {
    if (autoHideTimer.current) {
      clearTimeout(autoHideTimer.current);
      autoHideTimer.current = null;
    }
    setRefreshState({
      status: 'in-progress',
      startTime: new Date(),
      completionTime: null,
      triggeredBy,
      estimatedDurationMs,
    });
  }, []);

  const completeRefresh = useCallback(() => {
    setRefreshState((prev) => ({
      ...prev,
      status: 'complete',
      completionTime: new Date(),
    }));
    // Auto-fade to idle after 30 seconds
    autoHideTimer.current = setTimeout(() => {
      setRefreshState((prev) => {
        if (prev.status === 'complete') {
          return INITIAL_STATE;
        }
        return prev;
      });
      autoHideTimer.current = null;
    }, 30_000);
  }, []);

  const resetRefresh = useCallback(() => {
    if (autoHideTimer.current) {
      clearTimeout(autoHideTimer.current);
      autoHideTimer.current = null;
    }
    setRefreshState(INITIAL_STATE);
  }, []);

  // ─── Live mode polling ───────────────────────────────────────────────────────
  useEffect(() => {
    if (mode !== 'live') return;

    const poll = async () => {
      try {
        const statusResponse = await getForecastRunStatus();
        const currentState = refreshStateRef.current;

        if (statusResponse.status === 'in-progress' && currentState.status === 'idle') {
          const triggeredBy = statusResponse.triggeredBy ?? 'System';
          const estimatedMs = statusResponse.estimatedDurationMs ?? 0;
          setRefreshState({
            status: 'in-progress',
            startTime: statusResponse.startedAt ? new Date(statusResponse.startedAt) : new Date(),
            completionTime: null,
            triggeredBy,
            estimatedDurationMs: estimatedMs,
          });
        } else if (
          (statusResponse.status === 'completed' || statusResponse.status === 'idle') &&
          currentState.status === 'in-progress'
        ) {
          setRefreshState((prev) => ({
            ...prev,
            status: 'complete',
            completionTime: statusResponse.completedAt ? new Date(statusResponse.completedAt) : new Date(),
          }));
          autoHideTimer.current = setTimeout(() => {
            setRefreshState((prev) => {
              if (prev.status === 'complete') {
                return INITIAL_STATE;
              }
              return prev;
            });
            autoHideTimer.current = null;
          }, 30_000);
        }
      } catch {
        // Silently ignore polling errors — will retry on next interval
      }
    };

    // Run immediately on mount to detect in-progress runs
    poll();
    pollingRef.current = setInterval(poll, POLLING_INTERVAL_MS);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [mode]);

  return (
    <ForecastRefreshContext.Provider value={{ refreshState, startRefresh, completeRefresh, resetRefresh }}>
      {children}
    </ForecastRefreshContext.Provider>
  );
};

export const useForecastRefresh = (): ForecastRefreshContextType => {
  const ctx = useContext(ForecastRefreshContext);
  if (!ctx) {
    throw new Error('useForecastRefresh must be used within ForecastRefreshProvider');
  }
  return ctx;
};
